import React, { useEffect, useState } from 'react';
import { MainMatchesPrevisionData } from './MainMatchesData';
import { useNavigate } from 'react-router-dom';
import { FcOk } from 'react-icons/fc';
import { VscClose } from 'react-icons/vsc';
import { BsCheckCircleFill, BsXCircleFill } from 'react-icons/bs';
import { FaInfoCircle } from 'react-icons/fa';

// Importazioni PrimeReact
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { t } from 'i18next';

interface Props {
  data: MainMatchesPrevisionData[];
  renderItem?: (item: MainMatchesPrevisionData) => JSX.Element;
}

export const MatchList = ({ data }: Props) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [globalFilter, setGlobalFilter] = useState<string>('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 700);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Template for date/time
  const dateTemplate = (rowData: MainMatchesPrevisionData) => {
    const formattedDate = rowData.MatchDate.replace('T', ' ').slice(0, 16); // yyyy-MM-dd HH:mm
    const [datePart, time] = formattedDate.split(' '); // Divido data e ora
    const [year, month, day] = datePart.split('-'); // Estraggo giorno e mese

    return (
      <div className="flex flex-col text-center">
        <span className="font-bold text-white">
          {day}/{month}/{isMobile ? year.slice(2, 4) : year}
        </span>
        <span className="text-gray-300 text-xs">{time}</span>
      </div>
    );
  };

  // Template for match with results aligned
  const matchTemplate = (rowData: MainMatchesPrevisionData) => {
    return (
      <div className="match-container bg-slate-700 bg-opacity-40 p-2 rounded-lg shadow-sm">
        <div className="match-row">
          <div className="team-name home-team font-medium text-white">
            {rowData.Home}
          </div>
          <div className="team-score font-bold bg-slate-900 p-1 rounded min-w-6 text-center">
            {rowData.MatchResult ? rowData.MatchResult.split('-')[0] : '-'}
          </div>
        </div>
        <div className="match-row">
          <div className="team-name away-team text-gray-200">
            {rowData.Away}
          </div>
          <div className="team-score font-bold bg-slate-900 p-1 rounded min-w-6 text-center">
            {rowData.MatchResult ? rowData.MatchResult.split('-')[1] : '-'}
          </div>
        </div>
      </div>
    );
  };

  // Template for league and country info
  const leagueTemplate = (rowData: MainMatchesPrevisionData) => {
    return (
      <div className="flex flex-col items-center justify-center">
        <span className="font-medium text-white">{rowData.League}</span>
        <span className="text-xs text-gray-400">{rowData.Country}</span>
      </div>
    );
  };

  // Template for prediction
  const predictionTemplate = (rowData: MainMatchesPrevisionData) => {
    if (rowData.MatchResult == null) {
      return (
        <div className="prediction-container">
          <span className="bg-gradient-to-r from-teal-600 to-indigo-800 px-3 py-1 rounded-full text-white font-medium">
            {rowData.PredictionType === 'Result' ||
            rowData.PredictionType === 'ExactResult'
              ? rowData.ExactResult
              : rowData.PredictionType}
          </span>
          {rowData.Odd > 0 && (
            <span className="prediction-odd mt-0.2 bg-slate-700 rounded-full px-2 text-xs text-gray-300 inline-block">
              {rowData.Odd}
            </span>
          )}
        </div>
      );
    }

    const okIcon = <BsCheckCircleFill className="text-green-500" size={18} />;
    const errIcon = <BsXCircleFill className="text-red-500" size={18} />;

    const checkCorrect = (condition: boolean) =>
      condition ? <span>{okIcon}</span> : <span>{errIcon}</span>;

    if (rowData.PredictionType === 'Result') {
      return (
        <div className="prediction-result flex items-center justify-center space-x-2">
          <span className="bg-gradient-to-r from-teal-600 to-indigo-800 px-3 py-1 rounded-full text-white font-medium">
            {rowData.ExactResult}
          </span>
          <span className="prediction-icon">
            {checkCorrect(rowData.ExactResult === rowData.MatchResult)}
          </span>
        </div>
      );
    }

    const [homeGoals, awayGoals] = rowData.MatchResult.split('-').map(Number);
    let isCorrect = false;

    switch (rowData.PredictionType) {
      case 'Goal Goal':
        isCorrect = homeGoals > 0 && awayGoals > 0;
        break;
      case 'No Goal':
        isCorrect = homeGoals === 0 || awayGoals === 0;
        break;
      case 'Over 2.5':
        isCorrect = homeGoals + awayGoals > 2.5;
        break;
      case 'Under 3.5':
        isCorrect = homeGoals + awayGoals < 3.5;
        break;
      case 'X':
        isCorrect = homeGoals === awayGoals;
        break;
      case 'Multigoal 2-5':
        isCorrect = homeGoals + awayGoals >= 2 && homeGoals + awayGoals <= 5;
        break;
      case 'Multigoal 1-3':
        isCorrect = homeGoals + awayGoals >= 1 && homeGoals + awayGoals <= 3;
        break;
      case 'Multigoal 1-3 Home':
        isCorrect = homeGoals >= 1 && homeGoals <= 3;
        break;
      case 'Multigoal 1-3 Away':
        isCorrect = awayGoals >= 1 && awayGoals <= 3;
        break;
      case 'Under 1.5 Home':
        isCorrect = homeGoals < 1.5;
        break;
      case 'Under 1.5 Away':
        isCorrect = awayGoals < 1.5;
        break;
      case 'Over 1.5':
        isCorrect = homeGoals + awayGoals > 1.5;
        break;
      default:
        return rowData.PredictionType;
    }

    return (
      <div className="prediction-container">
        <div className="prediction-with-icon flex items-center justify-center space-x-2">
          <span className="bg-gradient-to-r from-teal-600 to-indigo-800 px-3 py-1 rounded-full text-white font-medium">
            {rowData.PredictionType}
          </span>
          <span className="prediction-icon">{checkCorrect(isCorrect)}</span>
        </div>
        {rowData.Odd > 0 && (
          <span className="prediction-odd mt-0.2 bg-slate-700 rounded-full px-2 text-xs text-gray-300 inline-block">
            {rowData.Odd}
          </span>
        )}
      </div>
    );
  };

  // Template for probability
  const probabilityTemplate = (rowData: MainMatchesPrevisionData) => {
    let probabilityClass = '';
    let textColor = '';

    if (rowData.Prob <= 25) {
      probabilityClass = 'bg-red-500';
      textColor = 'text-red-100';
    } else if (rowData.Prob < 40) {
      probabilityClass = 'bg-orange-500';
      textColor = 'text-orange-100';
    } else if (rowData.Prob <= 59) {
      probabilityClass = 'bg-yellow-500';
      textColor = 'text-yellow-100';
    } else if (rowData.Prob > 59 && rowData.Prob < 80) {
      probabilityClass = 'bg-green-500';
      textColor = 'text-green-100';
    } else {
      probabilityClass = 'bg-emerald-600';
      textColor = 'text-emerald-100';
    }

    return (
      <div className="cell-wrapper">
        <div className="cell-container bg-slate-800 border border-slate-700">
          <div
            className={`progress-bar ${probabilityClass}`}
            style={{ width: `${rowData.Prob}%` }}
          ></div>
          <div className={`cell-text-progressbar font-bold ${textColor}`}>
            {rowData.Prob}%
          </div>
        </div>
      </div>
    );
  };

  // Render actions button
  const actionsTemplate = (rowData: MainMatchesPrevisionData) => {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/MatchAnalysis/${rowData.MatchId}`);
        }}
        className="p-2 bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors duration-200 shadow-md"
      >
        <FaInfoCircle className="text-white" />
      </button>
    );
  };

  // Handle row click
  const onRowClick = (e: any) => {
    navigate(`/MatchAnalysis/${e.data.MatchId}`);
  };

  return (
    <div className="table-wrapper">
      <DataTable
        value={data}
        responsiveLayout="scroll"
        globalFilter={globalFilter}
        onRowClick={onRowClick}
        rowHover
        emptyMessage={t('ui.noMatchesFound')}
        className="custom-datatable text-xs sm:text-sm"
        sortMode="multiple"
        removableSort
        size="small"
        scrollable
        scrollHeight="flex"
        // rowClassName="cursor-pointer transition-colors duration-150 hover:bg-slate-700"
      >
        {!isMobile && (
          <Column
            field="League"
            header={t('matchList.competition')}
            sortable
            body={leagueTemplate}
            style={{ minWidth: '120px' }}
            className="centered-column"
            headerClassName="centered-header"
          />
        )}
        <Column
          field="MatchDate"
          header={t('matchList.date')}
          body={dateTemplate}
          sortable
          style={{ minWidth: '60px' }}
          className="centered-column"
          headerClassName="centered-header"
        />
        <Column
          field="Home"
          header={t('matchList.match')}
          body={matchTemplate}
          filterField="Home"
          filterPlaceholder={t('matchList.searchByTeam') as string}
          style={{ minWidth: '135px' }}
          className="centered-column"
          headerClassName="centered-header"
        />
        <Column
          field="PredictionType"
          header={t('matchList.prediction')}
          body={predictionTemplate}
          className="prediction-column centered-column"
          headerClassName="centered-header"
        />
        <Column
          field="Prob"
          header={t('matchList.probability')}
          body={probabilityTemplate}
          sortable
          style={{ minWidth: '120px' }}
          className="centered-column"
          headerClassName="centered-header"
        />
        {/* <Column
          body={actionsTemplate}
          style={{ width: '3rem' }}
          className="centered-column"
          headerClassName="centered-header"
        /> */}
      </DataTable>
    </div>
  );
};
